// src/components/PartnerLoginModal.jsx
import { useState, useEffect } from "react"
import { auth, db } from "../Firebase/firebase-config"
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth"
import { ref, get } from "firebase/database"
import { useNavigate } from "react-router-dom"
import { useAccount } from "../context/AccountContext"
import "./PartnerLogin.css";

const PartnerLoginModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const navigate = useNavigate()
  const { switchAccount } = useAccount()

  // ALL HOOKS FIRST — NEVER after return!
  useEffect(() => {
    if (!isOpen) return // Safe: effect still runs, but does nothing if closed

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return

      const mainUserId = await findMainUserId(user.uid)
      if (!mainUserId) {
        setError("You are not registered as a partner. Please use main login.")
        await auth.signOut()
        return
      }

      switchAccount(mainUserId)
      onClose?.()
      navigate("/partner-dashboard", { replace: true })
    })

    return () => unsubscribe()
  }, [isOpen, navigate, switchAccount, onClose])

  // Helper function
  const findMainUserId = async (partnerUid) => {
    try {
      const snapshot = await get(ref(db, "profileDashboard"))
      if (!snapshot.exists()) return null

      let mainUserId = null
      snapshot.forEach((mainSnap) => {
        const partners = mainSnap.child("partners").val()
        if (partners && partners[partnerUid]) {
          mainUserId = mainSnap.key
        }
      })
      return mainUserId
    } catch (err) {
      console.error("Error finding main user:", err)
      return null
    }
  }
// Helper: Check if a UID belongs to a main user (has profileDashboard entry)
const isMainUser = async (uid) => {
  try {
    const snapshot = await get(ref(db, `profileDashboard/${uid}`))
    return snapshot.exists()
  } catch (err) {
    console.error("Error checking main user:", err)
    return false
  }
}
  const handleLogin = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password)
      const uid = user.uid

      // 1. Is this a main user?
      if (await isMainUser(uid)) {
        setError("You are the main account holder. Partner login is only for authorized partners.")
        setLoading(false)
        return
      }

      // 2. Is this a valid partner?
      const mainUserId = await findMainUserId(uid)
      if (!mainUserId) {
        setError("You are not registered as a partner.")
        await auth.signOut()
        setLoading(false)
        return
      }

      // Valid partner → do nothing here
      // App.js will detect it and redirect to /partner-dashboard automatically
      onClose?.()

    } catch (err) {
      const msg =
        err.code === "auth/wrong-password" || err.code === "auth/invalid-credential"
          ? "Incorrect email or password"
          : err.code === "auth/user-not-found"
            ? "No account found with this email"
            : "Login failed. Please try again."
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async (e) => {
    e.preventDefault()
    if (!email) {
      setError("Please enter your email first.")
      return
    }
    try {
      await sendPasswordResetEmail(auth, email)
      setError("Password reset email sent! Check your inbox.")
    } catch (err) {
      setError("Failed to send reset email.")
    }
  }

  // EARLY RETURN — NOW AFTER ALL HOOKS → 100% VALID!
  if (!isOpen) return null

  return (
   <div className="partner-login-modal-backdrop" onClick={onClose}>
  <div className="partner-login-modal-content" onClick={(e) => e.stopPropagation()}>
    <div className="partner-login-modal-header">
      <h2>Partner Login</h2>
      <button className="partner-login-modal-close" onClick={onClose}>×</button>
    </div>

    <form onSubmit={handleLogin} className="partner-login-form-container">
      <div className="partner-login-input-group">
        <label className="partner-login-input-label">Email</label>
        <div className="partner-login-input-wrapper">
          <div className="partner-login-input-icon">
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ea8716" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
</div>
          <input
            type="email"
            className="partner-login-form-input"
            placeholder="partner@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
      </div>

     {/* PASSWORD FIELD WITH ORANGE ICONS */}
<div className="partner-login-input-group">
  <label className="partner-login-input-label">Password</label>
  <div className="partner-login-input-wrapper">
    {/* Lock Icon */}
    <div className="partner-login-input-icon">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ea8716" strokeWidth="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <circle cx="12" cy="16" r="1" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    </div>

    <input
      type={showPassword ? "text" : "password"}
      className="partner-login-form-input"
      placeholder="••••••••"
      value={password}
      onChange={(e) => setPassword(e.target.value)}
      required
      style={{ paddingRight: "50px" }}
    />

    {/* Eye / Eye-Off Toggle Button */}
    <button
      type="button"
      className="partner-login-password-toggle"
      onClick={() => setShowPassword(!showPassword)}
    >
      {showPassword ? (
        // Eye-Off (Hidden)
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ea8716" strokeWidth="2">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        // Eye (Visible)
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ea8716" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  </div>
</div>

      {error && (
            <div className="partner-login-notification-error">
              <span>{error}</span>

              {error.includes("main account holder") && (
                <div className="partner-login-main-user-actions">
                  <button
                    type="button"
                    onClick={() => { onClose?.(); navigate("/login") }}
                    className="partner-login-auth-button"
                    style={{ background: "#ea8716", marginTop: "12px" }}
                  >
                    Go to Main Login
                  </button>
                  <button
                    type="button"
                    onClick={() => { onClose?.(); navigate("/") }}
                    className="partner-login-secondary-button"
                  >
                    Back to Home
                  </button>
                </div>
              )}
            </div>
          )}

      <button
        type="submit"
        className="partner-login-auth-button"
        disabled={loading}
      >
        {loading ? <div className="partner-login-loading-spinner"></div> : "Login as Partner"}
      </button>

      <button type="button" className="partner-login-forgot-password" onClick={handleReset}>
        Forgot password?
      </button>

      <div className="partner-login-info-text">
        Only partners can log in here.<br />
        Main users should use regular login.
      </div>
    </form>
  </div>
</div>
  )
}

export default PartnerLoginModal